import { Card } from '@repo/ui/components/card'
import { Col } from '@repo/ui/components/col'
import { Row } from '@repo/ui/components/row'
import { Skeleton } from '@repo/ui/components/skeleton'

export default function LoadingPage() {
  return (
    <div className='min-h-screen bg-primary-main'>
      <div className='mx-auto max-w-container-xl w-full p-md'>
        <Row className='gap-lg items-start'>
          {/* Right Column - Property Details */}
          <Col className='flex-1 gap-lg'>
            {/* Navigation Tabs */}
            <Row className='gap-md my-2xl'>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className='w-20 h-2 rounded-md' />
              ))}
            </Row>

            {/* Header Section */}
            <Card className='p-lg dark:bg-neutral-surface'>
              <Row className='gap-lg items-start'>
                {/* Map/Image */}
                <Skeleton className='w-12 h-12 rounded-md' />

                {/* Title and Info */}
                <Col className='flex-1 gap-xs'>
                  <Skeleton className='w-3/4 h-6' />
                  <Skeleton className='w-1/2 h-4' />
                  <Skeleton className='w-1/3 h-4' />
                </Col>

                {/* Badge */}
                <Skeleton className='w-16 h-6 rounded-md' />
              </Row>
            </Card>

            {/* Price Chart */}
            <Card className='p-lg dark:bg-neutral-surface'>
              <Col className='gap-md'>
                <Row className='justify-between items-center'>
                  <Skeleton className='w-32 h-5' />
                  <Row className='gap-xs w-fit'>
                    <Skeleton className='w-12 h-6 rounded-md' />
                    <Skeleton className='w-12 h-6 rounded-md' />
                    <Skeleton className='w-12 h-6 rounded-md' />
                  </Row>
                </Row>
                <Skeleton className='w-full h-48 rounded-md' />
              </Col>
            </Card>

            {/* Documents */}
            <Card className='p-lg dark:bg-neutral-surface'>
              <Col className='gap-md'>
                <Skeleton className='w-32 h-5' />
                <Row className='gap-md flex-wrap'>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className='w-2/12 h-10 rounded-md' />
                  ))}
                </Row>
              </Col>
            </Card>

            {/* Consultations */}
            <Card className='p-lg dark:bg-neutral-surface'>
              <Col className='gap-md'>
                <Skeleton className='w-24 h-5' />
                <Row className='gap-md items-start'>
                  <Skeleton className='w-16 h-16 rounded-full' />
                  <Col className='flex-1 gap-xs'>
                    <Skeleton className='w-32 h-5' />
                    <Row className='gap-xs'>
                      <Skeleton className='w-6 h-6 rounded-md' />
                      <Skeleton className='w-6 h-6 rounded-md' />
                      <Skeleton className='w-6 h-6 rounded-md' />
                    </Row>
                  </Col>
                </Row>
                <Row className='gap-xs'>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className='w-3/4 h-24 rounded-md' />
                  ))}
                </Row>
              </Col>
            </Card>

            {/* Property Info */}
            <Card className='p-lg dark:bg-neutral-surface'>
              <Col className='gap-md'>
                <Skeleton className='w-32 h-5' />
                <Skeleton className='w-full h-xs rounded-md' />
                <Skeleton className='w-full h-xs rounded-md' />
                <Skeleton className='w-full h-xs rounded-md' />
                <Row className='gap-xs'>
                  <Skeleton className='w-full h-60 rounded-md' />
                  <Col className='gap-xs w-1/4'>
                    <Skeleton className='w-full h-30 rounded-md' />
                    <Skeleton className='w-full h-28 rounded-md' />
                  </Col>
                </Row>
              </Col>
            </Card>

            {/* Property Specifications */}
            <Card className='p-lg dark:bg-neutral-surface'>
              <Col className='gap-md'>
                <Skeleton className='w-40 h-5' />
                <Col className='gap-md'>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Row key={i} className='justify-between'>
                      <Skeleton className='w-full h-8 rounded-xs' />
                    </Row>
                  ))}
                </Col>
              </Col>
            </Card>
          </Col>

          {/* Left Column - Trading Form */}
          <Col className='w-full mt-4xl md:w-96 gap-md'>
            {/* Trading Form Card */}
            <Card className='p-lg dark:bg-neutral-surface'>
              <Col className='gap-lg'>
                {/* Header */}
                <Row className='justify-between items-center'>
                  <Skeleton className='w-32 h-5' />
                  <Skeleton className='w-4 h-4' />
                </Row>

                {/* Buy/Sell Toggle */}
                <Row className='gap-xs'>
                  <Skeleton className='w-16 h-8 rounded-full' />
                  <Skeleton className='w-16 h-8 rounded-full' />
                </Row>

                {/* Input Fields */}
                <Col className='gap-md'>
                  {/* Price Field */}
                  <Col className='gap-xs'>
                    <Skeleton className='w-40 h-4' />
                    <Skeleton className='w-full h-12 rounded-md' />
                  </Col>

                  {/* Amount Field */}
                  <Col className='gap-xs'>
                    <Skeleton className='w-32 h-4' />
                    <Skeleton className='w-full h-12 rounded-md' />
                  </Col>

                  {/* Balance Field */}
                  <Col className='gap-xs'>
                    <Skeleton className='w-24 h-4' />
                    <Row className='gap-xs items-center'>
                      <Skeleton className='flex-1 h-12 rounded-md' />
                      <Skeleton className='w-8 h-8 rounded-md' />
                      <Skeleton className='w-8 h-8 rounded-md' />
                    </Row>
                  </Col>

                  {/* Commission Field */}
                  <Col className='gap-xs'>
                    <Skeleton className='w-20 h-4' />
                    <Skeleton className='w-full h-12 rounded-md' />
                  </Col>

                  {/* Total Field */}
                  <Col className='gap-xs'>
                    <Skeleton className='w-24 h-4' />
                    <Skeleton className='w-full h-12 rounded-md' />
                  </Col>
                </Col>

                {/* Buy Button */}
                <Skeleton className='w-full h-12 rounded-md' />
              </Col>
            </Card>

            {/* Order Book */}
            <Card className='p-lg dark:bg-neutral-surface'>
              <Col className='gap-md'>
                <Skeleton className='w-24 h-5' />
                <Col className='gap-xs'>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Row key={i} className='justify-between'>
                      <Skeleton className='w-16 h-4' />
                      <Skeleton className='w-12 h-4' />
                    </Row>
                  ))}
                </Col>
              </Col>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}
